import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, BookOpen } from 'lucide-react';
import useLocalStorage from '../hooks/useLocalStorage';
import { getRankTone } from '../config/tiers';

const CATEGORIES = ['All', 'Breakfast', 'Lunch', 'Dinner', 'Dessert', 'Snack', 'Side', 'Drink'];
const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
const SOURCE_FILTERS = ['All', 'My Recipes', 'From Catalog'];

const Recipes = ({ recipes, addRecipe, removeRecipe, inventory, deductFromInventory, addToShoppingList, tier }) => {
  const lastMealType = (() => {
    try { return JSON.parse(localStorage.getItem('recipes.lastMealType')) || ''; }
    catch { return ''; }
  })();

  const [newRecipe, setNewRecipe] = useState({
    name: '',
    ingredients: [{ name: '', quantity: 0, unit: 'grams' }],
    procedure: [''],
    tags: lastMealType ? [lastMealType] : [],
    servings: 1,
    macros: { calories: '', protein: '', carbs: '', fat: '' },
    imageUrl: '',
  });
  const [expandedRecipe, setExpandedRecipe] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTag, setActiveTag] = useLocalStorage('recipes.activeTag', 'All');
  const [scaleFactors, setScaleFactors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [sourceFilter, setSourceFilter] = useLocalStorage('recipes.sourceFilter', 'All');
  const [catalogRecipes, setCatalogRecipes] = useState([]);
  const [addedCatalogIds, setAddedCatalogIds] = useState(new Set());
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetch('/.netlify/functions/catalog-recipes')
      .then((res) => res.ok ? res.json() : { recipes: [] })
      .then((data) => setCatalogRecipes(data.recipes || []))
      .catch(() => {});
  }, []);

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await fetch('/.netlify/functions/upload-image', {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (res.ok) {
        const data = await res.json();
        setNewRecipe((prev) => ({ ...prev, imageUrl: data.url }));
      }
    } catch {
      // Upload failed silently
    } finally {
      setUploading(false);
    }
  };

  const toggleTag = (tag) => {
    setNewRecipe((prev) => ({
      ...prev,
      tags: prev.tags.includes(tag)
        ? prev.tags.filter((t) => t !== tag)
        : [...prev.tags, tag],
    }));
  };

  const saveRecipe = () => {
    if (newRecipe.name.trim() && newRecipe.ingredients.some(ing => ing.name.trim())) {
      const macros = {
        calories: parseFloat(newRecipe.macros.calories) || 0,
        protein: parseFloat(newRecipe.macros.protein) || 0,
        carbs: parseFloat(newRecipe.macros.carbs) || 0,
        fat: parseFloat(newRecipe.macros.fat) || 0,
      };
      addRecipe({ ...newRecipe, servings: newRecipe.servings || 1, macros });
      const usedMealType = newRecipe.tags.find((t) => MEAL_TYPES.includes(t));
      if (usedMealType) {
        try { localStorage.setItem('recipes.lastMealType', JSON.stringify(usedMealType)); } catch {}
      }
      setNewRecipe({
        name: '',
        ingredients: [{ name: '', quantity: 0, unit: 'grams' }],
        procedure: [''],
        tags: usedMealType ? [usedMealType] : [],
        servings: 1,
        macros: { calories: '', protein: '', carbs: '', fat: '' },
        imageUrl: '',
      });
    } else {
      alert('Please enter a recipe name and at least one ingredient.');
    }
  };

  const addIngredientField = () => {
    setNewRecipe({
      ...newRecipe,
      ingredients: [...newRecipe.ingredients, { name: '', quantity: 0, unit: 'grams' }],
    });
  };

  const handleIngredientChange = (index, field, value) => {
    const updatedIngredients = [...newRecipe.ingredients];
    updatedIngredients[index] = { ...updatedIngredients[index], [field]: value };
    setNewRecipe({ ...newRecipe, ingredients: updatedIngredients });
  };

  const handleProcedureChange = (index, value) => {
    const updatedProcedure = [...newRecipe.procedure];
    updatedProcedure[index] = value;
    setNewRecipe({ ...newRecipe, procedure: updatedProcedure });
  };

  const addProcedureStep = () => {
    setNewRecipe({ ...newRecipe, procedure: [...newRecipe.procedure, ''] });
  };

  const calculateCost = (recipe) => {
    let total = 0;
    let hasPrice = false;
    recipe.ingredients.forEach((ing) => {
      const item = inventory.find(
        (i) => i.name.toLowerCase() === ing.name.toLowerCase()
      );
      if (item && item.price > 0 && item.quantity > 0) {
        hasPrice = true;
        total += (ing.quantity / item.quantity) * item.price;
      }
    });
    return hasPrice ? total : null;
  };

  const getScale = (index) => scaleFactors[index] || 1;

  const adjustScale = (index, delta) => {
    setScaleFactors((prev) => {
      const current = prev[index] || 1;
      const next = Math.max(0.25, Math.round((current + delta) * 4) / 4);
      return { ...prev, [index]: next };
    });
  };

  const handleCookRecipe = (recipe, recipeIndex) => {
    const scale = getScale(recipeIndex);
    const missingIngredients = recipe.ingredients.filter((ingredient) => {
      const inventoryItem = inventory.find(
        (item) => item.name.toLowerCase() === ingredient.name.toLowerCase()
      );
      return !inventoryItem || inventoryItem.quantity < ingredient.quantity * scale;
    });

    if (missingIngredients.length === 0) {
      const confirmCook = window.confirm(`Cook ${recipe.name} (x${scale})?`);
      if (confirmCook) {
        recipe.ingredients.forEach((ingredient) => {
          if (ingredient.name.trim() && ingredient.quantity > 0) {
            deductFromInventory(ingredient.name, ingredient.quantity * scale);
          }
        });
        alert(`You have cooked ${recipe.name}! Inventory updated.`);
      }
    } else {
      const missingNames = missingIngredients.map(ing => ing.name).join(', ');
      alert(`Missing ingredients: ${missingNames}. They have been added to your shopping list.`);
      missingIngredients.forEach(ing =>
        addToShoppingList(ing.name, Math.ceil(ing.quantity * scale))
      );
    }
  };

  const recordRecipeView = (name) => {
    if (!name) return;
    try {
      const raw = localStorage.getItem('recipes.recentlyViewed');
      const prev = raw ? JSON.parse(raw) : [];
      const filtered = prev.filter((n) => n.toLowerCase() !== name.toLowerCase());
      const next = [name, ...filtered].slice(0, 6);
      localStorage.setItem('recipes.recentlyViewed', JSON.stringify(next));
    } catch {}
  };

  const toggleRecipeExpansion = (key, recipe) => {
    const isOpening = expandedRecipe !== key;
    setExpandedRecipe(isOpening ? key : null);
    if (isOpening && recipe?.name) recordRecipeView(recipe.name);
  };

  const handleAddCatalogRecipe = (catRecipe) => {
    const personal = {
      name: catRecipe.name,
      ingredients: catRecipe.ingredients || [],
      procedure: catRecipe.procedure || [],
      tags: catRecipe.tags || [],
      servings: catRecipe.servings || 1,
      macros: catRecipe.macros || { calories: 0, protein: 0, carbs: 0, fat: 0 },
      imageUrl: catRecipe.image_url || '',
    };
    addRecipe(personal);
    setAddedCatalogIds((prev) => new Set([...prev, catRecipe.id]));
  };

  const personalNames = useMemo(() => {
    return new Set(recipes.map((r) => r.name.toLowerCase()));
  }, [recipes]);

  const filteredRecipes = useMemo(() => {
    const personal = recipes.map((r, i) => ({ ...r, _source: 'personal', _personalIndex: i }));

    const catalogOnly = catalogRecipes
      .filter((r) => !personalNames.has(r.name.toLowerCase()))
      .map((r) => ({
        name: r.name,
        ingredients: r.ingredients || [],
        procedure: r.procedure || [],
        tags: r.tags || [],
        servings: r.servings || 1,
        macros: r.macros || { calories: 0, protein: 0, carbs: 0, fat: 0 },
        imageUrl: r.image_url || '',
        _source: 'catalog',
        _catalogId: r.id,
      }));

    let combined;
    if (sourceFilter === 'My Recipes') combined = personal;
    else if (sourceFilter === 'From Catalog') combined = catalogOnly;
    else combined = [...personal, ...catalogOnly];

    return combined.filter((recipe) => {
      const matchesSearch =
        !searchQuery ||
        recipe.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        recipe.ingredients.some((ing) =>
          (ing.name || '').toLowerCase().includes(searchQuery.toLowerCase())
        );
      const matchesTag =
        activeTag === 'All' ||
        (recipe.tags && recipe.tags.includes(activeTag));
      return matchesSearch && matchesTag;
    });
  }, [recipes, catalogRecipes, personalNames, searchQuery, activeTag, sourceFilter]);

  return (
    <div className="page-container">
      <h1 className="page-title">Recipes</h1>

      <div className="form-group">
        <label htmlFor="recipe-name" className="form-label">Recipe Name</label>
        <input
          id="recipe-name"
          type="text"
          value={newRecipe.name}
          onChange={(e) => setNewRecipe({ ...newRecipe, name: e.target.value })}
          placeholder="e.g. Pasta Carbonara"
          className="form-input"
        />
      </div>

      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label htmlFor="recipe-servings" className="form-label">Servings</label>
          <input
            id="recipe-servings"
            type="number"
            min="1"
            value={newRecipe.servings}
            onChange={(e) =>
              setNewRecipe({ ...newRecipe, servings: parseInt(e.target.value) || 1 })
            }
            className="form-input"
          />
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <span className="form-label">Macros Per Serving (optional)</span>
        <div className="form-row">
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="macro-cal" className="sr-only">Calories</label>
            <input id="macro-cal" type="number" min="0" value={newRecipe.macros.calories} onChange={(e) => setNewRecipe({ ...newRecipe, macros: { ...newRecipe.macros, calories: e.target.value } })} placeholder="Calories" className="form-input" />
          </div>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="macro-pro" className="sr-only">Protein (g)</label>
            <input id="macro-pro" type="number" min="0" value={newRecipe.macros.protein} onChange={(e) => setNewRecipe({ ...newRecipe, macros: { ...newRecipe.macros, protein: e.target.value } })} placeholder="Protein (g)" className="form-input" />
          </div>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="macro-carb" className="sr-only">Carbs (g)</label>
            <input id="macro-carb" type="number" min="0" value={newRecipe.macros.carbs} onChange={(e) => setNewRecipe({ ...newRecipe, macros: { ...newRecipe.macros, carbs: e.target.value } })} placeholder="Carbs (g)" className="form-input" />
          </div>
          <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
            <label htmlFor="macro-fat" className="sr-only">Fat (g)</label>
            <input id="macro-fat" type="number" min="0" value={newRecipe.macros.fat} onChange={(e) => setNewRecipe({ ...newRecipe, macros: { ...newRecipe.macros, fat: e.target.value } })} placeholder="Fat (g)" className="form-input" />
          </div>
        </div>
      </div>

      <div className="form-group">
        <span className="form-label">Category</span>
        <div className="tag-list">
          {CATEGORIES.filter((c) => c !== 'All').map((cat) => (
            <button
              key={cat}
              type="button"
              className={`tag${newRecipe.tags.includes(cat) ? ' active' : ''}`}
              onClick={() => toggleTag(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3 className="section-title">Ingredients</h3>
        {newRecipe.ingredients.map((ingredient, index) => (
          <div key={index} className="form-row" style={{ marginBottom: '8px' }}>
            <div className="form-group" style={{ flex: 2, marginBottom: 0 }}>
              <label htmlFor={`ing-name-${index}`} className="sr-only">Ingredient name</label>
              <input
                id={`ing-name-${index}`}
                type="text"
                value={ingredient.name}
                onChange={(e) => handleIngredientChange(index, 'name', e.target.value)}
                placeholder="Ingredient name"
                className="form-input"
              />
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label htmlFor={`ing-qty-${index}`} className="sr-only">Quantity</label>
              <input
                id={`ing-qty-${index}`}
                type="number"
                value={ingredient.quantity}
                onChange={(e) =>
                  handleIngredientChange(index, 'quantity', parseInt(e.target.value) || 0)
                }
                placeholder="Qty"
                className="form-input"
              />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label htmlFor={`ing-unit-${index}`} className="sr-only">Unit</label>
              <select
                id={`ing-unit-${index}`}
                value={ingredient.unit}
                onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                className="form-select"
              >
                <option value="grams">grams</option>
                <option value="ml">mL</option>
                <option value="tsp">tsp</option>
                <option value="tbsp">tbsp</option>
              </select>
            </div>
          </div>
        ))}
        <button onClick={addIngredientField} className="btn btn-secondary btn-sm">
          + Add Ingredient
        </button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3 className="section-title">Procedure</h3>
        {newRecipe.procedure.map((step, index) => (
          <div key={index} className="form-group">
            <label htmlFor={`step-${index}`} className="sr-only">Step {index + 1}</label>
            <textarea
              id={`step-${index}`}
              value={step}
              onChange={(e) => handleProcedureChange(index, e.target.value)}
              placeholder={`Step ${index + 1}`}
              className="form-textarea"
            />
          </div>
        ))}
        <button onClick={addProcedureStep} className="btn btn-secondary btn-sm">
          + Add Step
        </button>
      </div>

      <div className="form-group">
        <label className="form-label">Recipe Image (optional)</label>
        {newRecipe.imageUrl && (
          <div style={{ marginBottom: '8px' }}>
            <img src={newRecipe.imageUrl} alt="Preview" style={{ maxWidth: '200px', maxHeight: '150px', borderRadius: 'var(--radius-md)', objectFit: 'cover' }} />
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={(e) => handleImageUpload(e.target.files?.[0])} style={{ display: 'none' }} />
        <button type="button" onClick={() => fileInputRef.current?.click()} className="btn btn-secondary btn-sm" disabled={uploading}>
          {uploading ? 'Uploading...' : newRecipe.imageUrl ? 'Change Image' : 'Upload Image'}
        </button>
      </div>

      <button onClick={saveRecipe} className="btn btn-primary btn-block">
        Save Recipe
      </button>

      {/* --- Saved Recipes Catalog --- */}
      <div className="catalog-section">
        <div className="catalog-header">
          <h2 className="catalog-title">Recipes</h2>
          <span className="catalog-count">{filteredRecipes.length} recipes</span>
        </div>

        <div className="recipe-source-tabs">
          {SOURCE_FILTERS.map((f) => (
            <button
              key={f}
              className={`recipe-source-tab${sourceFilter === f ? ' active' : ''}`}
              onClick={() => setSourceFilter(f)}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="search-bar">
          <Search size={16} aria-hidden="true" className="search-icon" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search recipes or ingredients..."
            className="form-input"
            aria-label="Search recipes"
          />
        </div>

        <div className="tag-list">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`tag${activeTag === cat ? ' active' : ''}`}
              onClick={() => setActiveTag(cat)}
            >
              {cat}
            </button>
          ))}
        </div>

        {filteredRecipes.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><BookOpen size={40} aria-hidden="true" /></div>
            <h3 className="empty-state-heading">
              {recipes.length === 0 && catalogRecipes.length === 0 ? 'No recipes yet' : 'No matches'}
            </h3>
            <p className="empty-state-text">
              {recipes.length === 0 && catalogRecipes.length === 0
                ? getRankTone(tier, 'recipesEmpty')
                : 'Try a different search term or category.'}
            </p>
          </div>
        ) : (
          <ul className="recipe-grid">
            {filteredRecipes.map((recipe, filteredIndex) => {
              const isPersonal = recipe._source === 'personal';
              const recipeKey = isPersonal ? `p-${recipe._personalIndex}` : `c-${recipe._catalogId}`;
              const scaleKey = isPersonal ? recipe._personalIndex : `cat-${recipe._catalogId}`;
              const scale = getScale(scaleKey);
              const cost = isPersonal ? calculateCost(recipe) : null;
              const isExpanded = expandedRecipe === recipeKey;
              const cal = recipe.macros?.calories ? Math.round(recipe.macros.calories) : null;
              return (
                <li
                  key={recipeKey}
                  className={`recipe-grid-item${isExpanded ? ' recipe-grid-item--expanded' : ''}`}
                >
                  {!isExpanded ? (
                    <div
                      className="card-recipe"
                      onClick={() => toggleRecipeExpansion(recipeKey, recipe)}
                      onKeyDown={(e) => e.key === 'Enter' && toggleRecipeExpansion(recipeKey, recipe)}
                      role="button"
                      tabIndex={0}
                      aria-expanded={false}
                    >
                      {recipe.imageUrl ? (
                        <div
                          className="card-recipe-thumb"
                          style={{ backgroundImage: `url(${recipe.imageUrl})` }}
                          aria-hidden="true"
                        />
                      ) : (
                        <div className="card-recipe-thumb card-recipe-thumb--placeholder" aria-hidden="true">
                          <BookOpen size={28} />
                        </div>
                      )}
                      <div className="card-recipe-body">
                        <h3 className="card-recipe-title">{recipe.name}</h3>
                        <div className="card-recipe-meta">
                          {cal !== null && <span>{cal} cal</span>}
                          {cal !== null && recipe.tags && recipe.tags[0] && <span className="card-recipe-meta-dot">·</span>}
                          {recipe.tags && recipe.tags[0] && <span>{recipe.tags[0]}</span>}
                          {cost !== null && (
                            <>
                              <span className="card-recipe-meta-dot">·</span>
                              <span>${cost.toFixed(2)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="card">
                  <div
                    className="card-header"
                    onClick={() => toggleRecipeExpansion(recipeKey, recipe)}
                    onKeyDown={(e) => e.key === 'Enter' && toggleRecipeExpansion(recipeKey, recipe)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={true}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span className="card-title">{recipe.name}</span>
                      <span className={`recipe-source-badge ${isPersonal ? 'recipe-source-personal' : 'recipe-source-catalog'}`}>
                        {isPersonal ? 'Your Recipe' : 'From Catalog'}
                      </span>
                      {recipe.tags && recipe.tags.map((t) => (
                        <span key={t} className="tag" style={{ cursor: 'default', fontSize: '11px', padding: '2px 8px' }}>
                          {t}
                        </span>
                      ))}
                      {cost !== null && (
                        <span className="cost-badge">${cost.toFixed(2)}</span>
                      )}
                    </div>
                    <span className="card-expand-icon expanded">
                      &#9660;
                    </span>
                  </div>
                  {(
                    <div className="card-body">
                      {recipe.imageUrl && (
                        <div style={{ marginBottom: '12px' }}>
                          <img src={recipe.imageUrl} alt={recipe.name} style={{ width: '100%', maxHeight: '240px', objectFit: 'cover', borderRadius: 'var(--radius-md)' }} loading="lazy" />
                        </div>
                      )}
                      <div className="scale-controls">
                        <span className="scale-label">Scale</span>
                        <button className="scale-btn" onClick={() => adjustScale(scaleKey, -0.25)} aria-label="Decrease scale">-</button>
                        <span className="scale-value">{scale}x</span>
                        <button className="scale-btn" onClick={() => adjustScale(scaleKey, 0.25)} aria-label="Increase scale">+</button>
                        {recipe.servings && (
                          <span style={{ fontSize: '13px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                            ({Math.round(recipe.servings * scale)} servings)
                          </span>
                        )}
                      </div>

                      <h4 className="recipe-sub-title">Ingredients</h4>
                      <ul className="recipe-ingredients-list">
                        {recipe.ingredients.map((ing, i) => (
                          <li key={i}>
                            {Math.round((ing.quantity || 0) * scale * 100) / 100} {ing.unit} of {ing.name}
                          </li>
                        ))}
                      </ul>

                      {cost !== null && (
                        <div style={{ marginBottom: '12px' }}>
                          <span className="cost-badge">
                            Estimated cost: ${(cost * scale).toFixed(2)}
                          </span>
                        </div>
                      )}

                      {recipe.macros && recipe.macros.calories > 0 && (
                        <div className="macro-badges" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                          <span className="tag active" style={{ cursor: 'default', fontSize: '11px', padding: '2px 8px' }}>{Math.round(recipe.macros.calories * scale)} cal</span>
                          <span className="tag" style={{ cursor: 'default', fontSize: '11px', padding: '2px 8px' }}>{Math.round(recipe.macros.protein * scale)}g P</span>
                          <span className="tag" style={{ cursor: 'default', fontSize: '11px', padding: '2px 8px' }}>{Math.round(recipe.macros.carbs * scale)}g C</span>
                          <span className="tag" style={{ cursor: 'default', fontSize: '11px', padding: '2px 8px' }}>{Math.round(recipe.macros.fat * scale)}g F</span>
                        </div>
                      )}

                      {recipe.procedure && recipe.procedure.length > 0 && recipe.procedure[0] !== '' && (
                        <>
                          <h4 className="recipe-sub-title">Procedure</h4>
                          <ol className="recipe-procedure-list">
                            {recipe.procedure.map((step, i) => (
                              <li key={i}>{step}</li>
                            ))}
                          </ol>
                        </>
                      )}
                      <div className="card-actions">
                        {isPersonal ? (
                          <>
                            <button onClick={() => handleCookRecipe(recipe, recipe._personalIndex)} className="btn btn-primary btn-sm">
                              Cook
                            </button>
                            <button onClick={() => removeRecipe(recipe._personalIndex)} className="btn btn-danger btn-sm">
                              Delete
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => handleCookRecipe(recipe, filteredIndex)} className="btn btn-primary btn-sm">
                              Cook
                            </button>
                            <button
                              onClick={() => handleAddCatalogRecipe(catalogRecipes.find((c) => c.id === recipe._catalogId))}
                              className={`btn btn-sm ${addedCatalogIds.has(recipe._catalogId) ? 'btn-secondary' : 'btn-primary'}`}
                              disabled={addedCatalogIds.has(recipe._catalogId)}
                            >
                              {addedCatalogIds.has(recipe._catalogId) ? 'Added' : 'Add to My Recipes'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Recipes;
